import type { IconType } from "react-icons";
import {
  RiAddCircleLine,
  RiArrowRightLine,
  RiEraserLine,
  RiExchangeLine,
  RiImageAddLine,
  RiMagicLine,
  RiStrikethrough,
  RiText,
  RiTranslate2,
} from "react-icons/ri";
import { useTranslation } from "../i18n";
import type { AreaOpRow, AreaOpType } from "../lib/areaOpsPrompt";

const ICONS: Record<AreaOpType, IconType> = {
  "replace-product": RiExchangeLine,
  "remove-object": RiEraserLine,
  "remove-text": RiStrikethrough,
  translate: RiTranslate2,
  "change-image-text": RiText,
  "edit-region": RiMagicLine,
  "add-object": RiAddCircleLine,
  "add-logo": RiImageAddLine,
};

function OpDetail({ op }: { op: AreaOpRow }) {
  if (op.to !== undefined) {
    return (
      <span className="flex items-center gap-1.5 min-w-0 flex-wrap">
        {op.from && (
          <span className="text-xs text-gray-400 line-through break-words">{op.from}</span>
        )}
        <RiArrowRightLine className="w-3 h-3 shrink-0 text-gray-400" />
        <span className="text-xs font-medium text-gray-800 break-words">{op.to}</span>
      </span>
    );
  }
  if (!op.text) return null;
  return (
    <span className="text-xs text-gray-700 leading-snug break-words whitespace-pre-wrap">
      {op.text}
    </span>
  );
}

// Batch area-ops prompt ("Toplu Düzenleme") as a numbered list of ops.
export default function AreaOpsPromptList({ ops }: { ops: AreaOpRow[] }) {
  const { t } = useTranslation();
  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-white text-sm">
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-gray-500 text-[11px] tracking-[0.08em] uppercase">
          {t("areaOps.titleUpper")}
        </p>
        <span className="text-[10px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
          {t("areaOps.count", { count: ops.length })}
        </span>
      </div>
      <ol className="space-y-1.5 max-h-56 overflow-y-auto">
        {ops.map((op, i) => {
          const Icon = ICONS[op.type];
          return (
            <li
              key={i}
              className="flex items-start gap-2 px-2 py-1.5 rounded-md bg-gray-50 border border-gray-100"
            >
              <span className="shrink-0 w-5 h-5 mt-px rounded-full bg-purple-100 text-purple-700 text-[10px] font-semibold flex items-center justify-center tabular-nums">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="inline-flex items-center gap-1 shrink-0 text-[10px] font-medium text-purple-700 bg-purple-50 border border-purple-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                  <Icon className="w-3 h-3" />
                  {t(`areaOps.types.${op.type}`)}
                </span>
                <OpDetail op={op} />
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
